import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
    getBroadcastsBot, getFunisBot, getRecortesBot, getResumoBot, getSerieBot,
    type CanalBot, type DimensaoRecorte, type FiltrosBot, type GranularidadeSerie, type PeriodoBot,
} from '@/services/api/analytics-bot';
import { getActiveMetaPages } from '@/services/api/connections';
import { getCurrentUser } from '@/services/api/auth';
import FiltrosBotBar from './components/FiltrosBot';
import CardsReceita from './components/CardsReceita';
import DadosTotais from './components/DadosTotais';
import GraficoCliques from './components/GraficoCliques';
import ListaFunis from './components/ListaFunis';
import ListaBroadcasts from './components/ListaBroadcasts';
import Recortes from './components/Recortes';
import Avisos from './components/Avisos';
import { MAX_DIAS_CUSTOM, PERIODOS, dataCurta, intervaloLocal, n, somaDias } from './format';
import { paisDaPagina } from './paises';
import { useFiltroUsuarioAdmin } from '@/components/FiltroUsuarioAdmin';
import './analyticsBot.css';

/**
 * Analytics do Bot V1 — rolagem única, sete blocos (guia §2).
 *
 * Uma query por bloco, em paralelo, todas com os filtros na chave: trocar
 * filtro ou período refaz tudo, inclusive o funil/broadcast que estiver aberto.
 * O detalhe do funil e as quebras do broadcast só carregam ao abrir a linha.
 */

const STALE = 60_000;

export default function AnalyticsBot() {
    const isAdmin = getCurrentUser()?.role === 'admin';

    const [periodo, setPeriodo] = useState<PeriodoBot>('hoje');
    const hoje = intervaloLocal('hoje').ate;
    const [deCustom, setDeCustom] = useState(() => intervaloLocal('7d').de);
    const [ateCustom, setAteCustom] = useState(hoje);
    const [paginaId, setPaginaId] = useState<string | null>(null);
    const [pais, setPais] = useState<string | null>(null);
    const [dominio, setDominio] = useState<string | null>(null);
    const { usuarioId: userId } = useFiltroUsuarioAdmin();

    const [granul, setGranul] = useState<GranularidadeSerie>('auto');
    const [canalGraf, setCanalGraf] = useState<CanalBot>('ambos');
    const [dimRec, setDimRec] = useState<DimensaoRecorte>('pagina');
    const [canalRec, setCanalRec] = useState<CanalBot>('ambos');
    const [broadAberto, setBroadAberto] = useState<number | null>(null);

    const custom = periodo === 'custom';
    const filtros: FiltrosBot = useMemo(() => ({
        periodo,
        ...(custom ? { de: deCustom, ate: ateCustom } : {}),
        pagina_id: paginaId,
        pais,
        dominio,
        user_id: isAdmin ? userId : null,
    }), [periodo, custom, deCustom, ateCustom, paginaId, pais, dominio, userId, isAdmin]);

    // Mantém o período personalizado dentro do teto do backend, mexendo na outra ponta.
    const mudarDe = (v: string) => {
        setDeCustom(v);
        if (ateCustom < v) setAteCustom(v);
        else if (somaDias(v, MAX_DIAS_CUSTOM - 1) < ateCustom) setAteCustom(somaDias(v, MAX_DIAS_CUSTOM - 1));
    };
    const mudarAte = (v: string) => {
        setAteCustom(v);
        if (deCustom > v) setDeCustom(v);
        else if (somaDias(v, -(MAX_DIAS_CUSTOM - 1)) > deCustom) setDeCustom(somaDias(v, -(MAX_DIAS_CUSTOM - 1)));
    };

    const umDia = periodo === 'hoje' || periodo === 'ontem' || (custom && deCustom === ateCustom);
    const granulEf: GranularidadeSerie = granul === 'dia' && umDia ? 'auto' : granul;

    const paginasQ = useQuery({ queryKey: ['meta-pages-active'], queryFn: getActiveMetaPages, staleTime: 5 * 60_000 });

    // Logo após o deploy o backend ainda materializa os dias em background e
    // responde com campos null + dados_pendentes; recarrega até completar.
    const comum = {
        placeholderData: keepPreviousData,
        staleTime: STALE,
        refetchInterval: (q: { state: { data?: { dados_pendentes?: boolean } } }) =>
            q.state.data?.dados_pendentes ? 15_000 : false,
    } as const;
    const resumoQ = useQuery({ queryKey: ['analytics-bot', 'resumo', filtros], queryFn: () => getResumoBot(filtros), ...comum });
    const serieQ = useQuery({ queryKey: ['analytics-bot', 'serie', filtros, granulEf], queryFn: () => getSerieBot(filtros, granulEf), ...comum });
    const funisQ = useQuery({ queryKey: ['analytics-bot', 'funis', filtros], queryFn: () => getFunisBot(filtros), ...comum });
    const broadQ = useQuery({ queryKey: ['analytics-bot', 'broadcasts', filtros], queryFn: () => getBroadcastsBot(filtros), ...comum });
    const recQ = useQuery({
        queryKey: ['analytics-bot', 'recortes', filtros, dimRec, canalRec],
        queryFn: () => getRecortesBot(filtros, dimRec, canalRec),
        ...comum,
    });

    // Opções de país = países com dado no período, não todo país de página ativa
    // (página ativa parada há semanas deixava o filtro oferecer país vazio).
    const semPaisNemPagina = useMemo(() => ({ ...filtros, pais: null, pagina_id: null }), [filtros]);
    const paisesQ = useQuery({
        queryKey: ['analytics-bot', 'paises', semPaisNemPagina],
        queryFn: () => getRecortesBot(semPaisNemPagina, 'pais', 'ambos'),
        placeholderData: keepPreviousData,
        staleTime: STALE,
    });

    const semDominioNemPagina = useMemo(() => ({ ...filtros, dominio: null, pagina_id: null }), [filtros]);
    const dominiosQ = useQuery({
        queryKey: ['analytics-bot', 'dominios', semDominioNemPagina],
        queryFn: () => getRecortesBot(semDominioNemPagina, 'dominio', 'ambos'),
        placeholderData: keepPreviousData,
        staleTime: STALE,
    });

    const resumo = resumoQ.data;
    const periodoSrv = resumo && !resumoQ.isPlaceholderData ? resumo.periodo : null;
    const { de, ate } = periodoSrv ?? (custom ? { de: deCustom, ate: ateCustom } : intervaloLocal(periodo));
    const diaParcial = periodoSrv ? periodoSrv.dia_parcial : periodo === 'hoje' || (custom && ateCustom === hoje);
    const rotulo = custom
        ? (deCustom === ateCustom ? dataCurta(deCustom) : `${dataCurta(deCustom)} a ${dataCurta(ateCustom)}`)
        : (PERIODOS.find(p => p.k === periodo)?.l ?? '').toLowerCase();

    const paginas = paginasQ.data ?? [];
    const nomePagina = paginaId ? paginas.find(p => p.page_id === paginaId)?.page_name ?? paginaId : null;
    const nomePais = pais
        ? paginas.map(p => paisDaPagina(p.language)).find(x => x?.codigo === pais)?.nome ?? pais
        : null;
    const recorte = [nomePagina, nomePais, dominio].filter(Boolean).map(x => ` · ${x}`).join('');
    const opcoesPais = useMemo(() => {
        const lista = (paisesQ.data?.linhas ?? [])
            .filter(l => l.chave && l.chave !== 'XX')
            .map(l => ({ codigo: l.chave, nome: l.nome }));
        // O país escolhido continua no seletor mesmo se o período novo não tiver dado dele.
        if (pais && !lista.some(x => x.codigo === pais)) lista.push({ codigo: pais, nome: nomePais ?? pais });
        return lista.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    }, [paisesQ.data, pais, nomePais]);
    const opcoesDominio = useMemo(() => {
        const s = new Set((dominiosQ.data?.linhas ?? []).map(l => l.chave).filter(Boolean));
        if (dominio) s.add(dominio);
        return [...s].sort();
    }, [dominiosQ.data, dominio]);

    const funis = funisQ.data?.funis;
    const broads = broadQ.data?.broadcasts;
    const envios = broads ? broads.reduce((a, b) => a + ((b.enviados ?? 0) > 0 ? b.ocorrencias : 0), 0) : null;
    const dias = periodoSrv?.dias ?? (umDia ? 1 : null);

    const atualizando = (q: { isFetching: boolean; isPlaceholderData: boolean }) => q.isFetching && q.isPlaceholderData;

    const limpar = () => {
        setPeriodo('hoje');
        setPaginaId(null);
        setPais(null);
        setDominio(null);
    };

    return (
        <div className="bx">
            <div className="top">
                <div>
                    <h1>Analytics do bot</h1>
                    <div className="sub">
                        {funis ? `${funis.length} funis` : '— funis'} · {broads ? `${broads.length} disparos` : '— disparos'}
                        {dias !== null && dias > 1 && ` · ${dias} dias`}
                        {envios !== null && !umDia && ` · ${n(envios)} envios`}
                    </div>
                </div>
                <div className="sub num">
                    {dataCurta(de)}{de !== ate && ` – ${dataCurta(ate)}`}
                    {diaParcial && ` · dia parcial${typeof periodoSrv?.hora_corrente === 'number' ? ` até ${String(periodoSrv.hora_corrente).padStart(2, '0')}h` : ''}`}
                </div>
            </div>

            <FiltrosBotBar
                periodo={periodo} de={deCustom} ate={ateCustom} hoje={hoje}
                paginaId={paginaId} pais={pais} dominio={dominio} userId={userId}
                isAdmin={isAdmin} paginas={paginas} paises={opcoesPais} dominios={opcoesDominio}
                onPeriodo={setPeriodo} onDe={mudarDe} onAte={mudarAte} onPagina={setPaginaId} onPais={setPais} onDominio={setDominio}
                onLimpar={limpar}
            />

            <div data-loading={atualizando(resumoQ) ? 1 : 0}>
                <CardsReceita
                    resumo={resumo}
                    rotulo={rotulo}
                    funisRastreados={funis ? { rastreados: funis.filter(f => f.rastreio_receita).length, total: funis.length } : null}
                    disparos={envios}
                />
            </div>
            {resumoQ.isError && !resumo && (
                <div className="card"><Avisos avisos={['Não foi possível carregar o resumo de receita.']} /></div>
            )}

            <DadosTotais
                resumo={resumo} rotulo={rotulo} recorte={recorte}
                carregando={resumoQ.isLoading} atualizando={atualizando(resumoQ)} erro={resumoQ.isError}
                onTentar={() => resumoQ.refetch()}
            />

            <GraficoCliques
                serie={serieQ.data} granul={granulEf} canal={canalGraf} umDia={umDia} diaParcial={diaParcial}
                carregando={serieQ.isLoading} atualizando={atualizando(serieQ)} erro={serieQ.isError}
                onTentar={() => serieQ.refetch()} onGranul={setGranul} onCanal={setCanalGraf}
            />

            <ListaFunis
                dados={funisQ.data} filtros={filtros} de={de} ate={ate} rotulo={rotulo}
                carregando={funisQ.isLoading} atualizando={atualizando(funisQ)} erro={funisQ.isError}
                onTentar={() => funisQ.refetch()}
            />

            <ListaBroadcasts
                dados={broadQ.data} filtros={filtros} rotulo={rotulo} variosDias={!umDia}
                aberto={broadAberto} onAbrir={setBroadAberto}
                carregando={broadQ.isLoading} atualizando={atualizando(broadQ)} erro={broadQ.isError}
                onTentar={() => broadQ.refetch()}
            />

            <Recortes
                dados={recQ.data} rotulo={rotulo} dimensao={dimRec} canal={canalRec}
                onDimensao={setDimRec} onCanal={setCanalRec}
                paises={opcoesPais} pais={pais} onPais={v => { setPais(v); setPaginaId(null); }}
                carregando={recQ.isLoading} atualizando={atualizando(recQ)} erro={recQ.isError}
                onTentar={() => recQ.refetch()}
            />
        </div>
    );
}
